import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PenLine, Eye, Heart, Clock, Plus, Edit, Trash2, BookOpen, Download } from 'lucide-react';
import ExportMenu from '../components/ExportMenu';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const MyStoriesPage = () => {
  const { api } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const res = await api.get('/stories/my?include_drafts=true');
        setStories(res.data);
      } catch (error) {
        console.error('Failed to fetch my stories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStories();
  }, [api]);

  const handleDelete = async (storyId) => {
    try {
      await api.delete(`/stories/${storyId}`);
      setStories(stories.filter(s => s.id !== storyId));
    } catch (error) {
      console.error('Failed to delete story:', error);
    }
  };

  const filteredStories = stories.filter(story => {
    if (activeTab === 'all') return true;
    if (activeTab === 'published') return !story.is_draft;
    if (activeTab === 'drafts') return story.is_draft;
    return true;
  });

  const draftCount = stories.filter(s => s.is_draft).length;
  const publishedCount = stories.filter(s => !s.is_draft).length;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="my-stories-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-text-primary mb-2">My Works</h1>
          <p className="text-text-secondary">
            {stories.length} works • {publishedCount} published • {draftCount} drafts
          </p>
        </div>
        <Link to="/write" data-testid="new-story-button">
          <Button className="btn-accent gap-2">
            <Plus className="w-4 h-4" />
            New Work
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-surface-alt">
          <TabsTrigger value="all" data-testid="tab-all">All ({stories.length})</TabsTrigger>
          <TabsTrigger value="published" data-testid="tab-published">Published ({publishedCount})</TabsTrigger>
          <TabsTrigger value="drafts" data-testid="tab-drafts">Drafts ({draftCount})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Stories List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : filteredStories.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4" />
          <h2 className="font-serif text-2xl font-semibold text-text-primary mb-2">
            {activeTab === 'all' ? 'No stories yet' : `No ${activeTab} stories`}
          </h2>
          <p className="text-text-secondary mb-4">
            {activeTab === 'all' 
              ? 'Start your writing journey today!' 
              : activeTab === 'drafts'
              ? 'All your stories are published!'
              : 'Publish a story to see it here.'}
          </p>
          {activeTab === 'all' && (
            <Link to="/write">
              <Button className="btn-primary">Write Your First Story</Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredStories.map((story) => (
            <Card 
              key={story.id} 
              className="bg-white border-brand-primary/10"
              data-testid={`story-item-${story.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-6">
                  {story.cover_image && (
                    <img 
                      src={story.cover_image}
                      alt={story.title}
                      className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
                      style={{ objectPosition: `center ${story.cover_image_position ?? 50}%` }}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      {story.is_draft ? (
                        <Badge className="bg-amber-100 text-amber-700">Draft</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">Published</Badge>
                      )}
                      <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">
                        {story.genre}
                      </span>
                      {story.challenge_id && (
                        <Badge className="bg-brand-accent/10 text-brand-accent">Challenge Entry</Badge>
                      )}
                      {story.challenge_hidden && (
                        <Badge className="bg-purple-100 text-purple-700" data-testid={`hidden-badge-${story.id}`}>Hidden (Blind Judging)</Badge>
                      )}
                      {(story.content?.match(/\[chapter:/g) || []).length > 0 && (
                        <Badge className="bg-blue-50 text-blue-600">
                          {(story.content.match(/\[chapter:/g) || []).length} chapters
                        </Badge>
                      )}
                    </div>

                    <h3 className="font-serif text-xl font-semibold text-text-primary mb-1">
                      {story.title}
                    </h3>

                    {story.summary && (
                      <p className="text-text-secondary text-sm line-clamp-2 mb-3">
                        {story.summary}
                      </p>
                    )}

                    <div className="flex items-center gap-4 text-sm text-text-muted">
                      <span className="flex items-center gap-1">
                        <Eye className="w-4 h-4" /> {story.view_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart className="w-4 h-4" /> {story.like_count}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        Updated {new Date(story.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Link to={`/story/${story.id}/edit`}>
                      <Button variant="outline" size="icon" data-testid={`edit-story-${story.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                    {!story.is_draft && (
                      <Link to={`/story/${story.id}`}>
                        <Button variant="outline" size="icon" data-testid={`view-story-${story.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                    )}
                    {!story.is_draft && (
                      <ExportMenu storyId={story.id} />
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon" className="text-red-500 hover:text-red-600" data-testid={`delete-story-${story.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Story?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{story.title}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDelete(story.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyStoriesPage;
